import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import AvatarImg from '../../../components/AvatarImg';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
const fmtTime = (t) => t ? t.slice(0, 5) : '';

const STATUS_STYLES = {
  scheduled: { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6', key: 'match.upcoming.scheduled', fallback: 'Planifié' },
  live: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444', key: 'match.widget.inProgress', fallback: 'EN COURS' },
};

const UpcomingMatchesSection = ({ tiktokId }) => {
  const { t } = useTranslation();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tiktokId) return;
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        // Find matches where this creator participates
        const { data: parts } = await supabase
          .from('match_participants')
          .select('official_match_id')
          .eq('tiktok_id', tiktokId);

        if (!parts || parts.length === 0) { setMatches([]); return; }

        const matchIds = [...new Set(parts.map(p => p.official_match_id))];
        const { data: matchesData } = await supabase
          .from('official_matches')
          .select('id, match_date, match_time, match_format, status, responsible_manager_name, tournament_id, tournament_round')
          .in('id', matchIds)
          .gte('match_date', today)
          .in('status', ['scheduled', 'live'])
          .order('match_date', { ascending: true })
          .limit(5);

        if (!matchesData || matchesData.length === 0) { setMatches([]); return; }

        // Get participants
        const { data: allParts } = await supabase
          .from('match_participants')
          .select('official_match_id, team, tiktok_username, display_name, avatar_url, is_agency_creator')
          .in('official_match_id', matchesData.map(m => m.id));

        const partMap = {};
        (allParts || []).forEach(p => {
          if (!partMap[p.official_match_id]) partMap[p.official_match_id] = [];
          partMap[p.official_match_id].push(p);
        });

        setMatches(matchesData.map(m => ({
          ...m,
          teamA: (partMap[m.id] || []).filter(p => p.team === 'A'),
          teamB: (partMap[m.id] || []).filter(p => p.team === 'B'),
        })));
      } catch (err) {
        console.error('UpcomingMatchesSection error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [tiktokId]);

  if (loading) return null;
  if (matches.length === 0) return null;

  return (
    <div style={{ borderRadius: 12, border: '1px solid #1f1f1f', backgroundColor: '#111111', overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #1f1f1f', display: 'flex', alignItems: 'center', gap: 8 }}>
        <svg width="14" height="14" viewBox="0 0 18 18" fill="none" style={{ color: '#E5202A' }}>
          <path d="M9 1L2 5v6l7 4 7-4V5L9 1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M5.5 9l2.5 2.5 4-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: 1, textTransform: 'uppercase', fontFamily: 'Sora, sans-serif' }}>
          {t('match.upcoming.title', 'Matchs à venir')}
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, backgroundColor: 'rgba(229,32,42,0.15)', color: '#E5202A' }}>
          {matches.length}
        </span>
      </div>
      {matches.map((m, idx) => {
        const st = STATUS_STYLES[m.status] || STATUS_STYLES.scheduled;
        return (
          <div key={m.id} style={{ padding: '10px 16px', borderTop: idx > 0 ? '1px solid #1a1a1a' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#E5202A', fontFamily: 'Sora, sans-serif' }}>
                {fmtDate(m.match_date)} · {fmtTime(m.match_time)}
              </span>
              <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>
                {m.match_format}
              </span>
              {m.tournament_id && (
                <span style={{ fontSize: 8, fontWeight: 700, padding: '2px 5px', borderRadius: 4, backgroundColor: 'rgba(255,215,0,0.15)', color: '#FFD700' }}>{t('match.upcoming.tournament', 'TOURNOI')}</span>
              )}
              <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 10, backgroundColor: st.bg, color: st.color }}>
                {t(st.key, st.fallback)}
              </span>
            </div>
            {m.match_format === '2v2' ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {[...(m.teamA.length ? m.teamA : [null]), ...(m.teamA.length < 2 ? [null] : [])].slice(0, 2).map((p, i) => (
                  <div key={`a${i}`} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 8px', borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(59,130,246,0.15)' }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', overflow: 'hidden', border: '1.5px solid #3b82f6', backgroundColor: '#222', flexShrink: 0 }}>
                      {p && <AvatarImg src={p.avatar_url} username={p.tiktok_username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p?.display_name || p?.tiktok_username || 'TBD'}</div>
                      {p && <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.3)' }}>@{p.tiktok_username}</div>}
                    </div>
                    {p?.is_agency_creator && <span style={{ fontSize: 7, fontWeight: 700, padding: '1px 3px', borderRadius: 2, backgroundColor: 'rgba(229,32,42,0.15)', color: '#E5202A', flexShrink: 0 }}>AG</span>}
                  </div>
                ))}
                {[...(m.teamB.length ? m.teamB : [null]), ...(m.teamB.length < 2 ? [null] : [])].slice(0, 2).map((p, i) => (
                  <div key={`b${i}`} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 8px', borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(236,72,153,0.15)' }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', overflow: 'hidden', border: '1.5px solid #ec4899', backgroundColor: '#222', flexShrink: 0 }}>
                      {p && <AvatarImg src={p.avatar_url} username={p.tiktok_username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p?.display_name || p?.tiktok_username || 'TBD'}</div>
                      {p && <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.3)' }}>@{p.tiktok_username}</div>}
                    </div>
                    {p?.is_agency_creator && <span style={{ fontSize: 7, fontWeight: 700, padding: '1px 3px', borderRadius: 2, backgroundColor: 'rgba(229,32,42,0.15)', color: '#E5202A', flexShrink: 0 }}>AG</span>}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {m.teamA.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', overflow: 'hidden', border: p.is_agency_creator ? '1.5px solid #E5202A' : '1px solid #2a2a2a', backgroundColor: '#222', flexShrink: 0 }}>
                      <AvatarImg src={p.avatar_url} username={p.tiktok_username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>@{p.tiktok_username}</span>
                  </div>
                ))}
                <span style={{ fontSize: 9, fontWeight: 900, color: 'rgba(255,255,255,0.2)', margin: '0 2px' }}>VS</span>
                {m.teamB.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', overflow: 'hidden', border: '1px solid #2a2a2a', backgroundColor: '#222', flexShrink: 0 }}>
                      <AvatarImg src={p.avatar_url} username={p.tiktok_username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>@{p.tiktok_username}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 4 }}>
              {t('match.upcoming.responsible', 'Responsable')} : {m.responsible_manager_name || '—'}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default UpcomingMatchesSection;
